import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { NAMED_KO_ROUNDS } from '@/core/engine/formats/singleElimination/bracket';
import {
  areSettingsEditable,
  isRegistrationOpen,
  uidSchema,
  type MatchRuleSet,
  type MatchRulesSettings,
  type RoundKey,
  type TournamentSettings,
} from '@/core/schemas';
import { useAuth } from '@/app/providers/auth/authContext';
import { describeError } from '@/features/team/describeError';
import { useTournamentData } from '@/features/tournament/useTournamentData';
import { saveTournamentSettings } from '@/infrastructure/repositories/settingsRepository';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { FormField } from '@/shared/ui/FormField';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PageState } from '@/shared/ui/PageState';

/** Reads a numeric config value defensively — phase config is typed `unknown`. */
function numberFrom(config: unknown, key: string, fallback: number): number {
  if (typeof config === 'object' && config !== null && key in config) {
    const value = (config as Record<string, unknown>)[key];
    return typeof value === 'number' ? value : fallback;
  }
  return fallback;
}

export function AdminSettingsPage() {
  usePageTitle('Einstellungen');
  const { session } = useAuth();
  const { tournament, isLoading, errorMessage } = useTournamentData();
  const [draft, setDraft] = useState<TournamentSettings | null>(null);
  const [saved, setSaved] = useState(false);

  const actorUid = session.status === 'authenticated' ? uidSchema.parse(session.account.uid) : null;

  const save = useMutation({
    mutationFn: (settings: TournamentSettings) => {
      if (tournament === undefined || actorUid === null) throw new Error('Nicht möglich');
      return saveTournamentSettings({ tournament, settings, actorUid });
    },
    onSuccess: () => setSaved(true),
  });

  if (isLoading) {
    return <LoadingScreen label="Einstellungen werden geladen …" />;
  }

  if (errorMessage !== null || tournament === undefined) {
    return <PageState title="Einstellungen nicht verfügbar" description={errorMessage ?? ''} />;
  }

  if (!areSettingsEditable(tournament.status)) {
    return (
      <Alert tone="info">
        Das Turnier läuft bereits. Einstellungen können nur vor dem Turnierstart geändert werden.
      </Alert>
    );
  }

  const settings = draft ?? tournament.settings;

  const patch = (next: Partial<TournamentSettings>) => {
    setSaved(false);
    setDraft({ ...settings, ...next });
  };

  const groupPhaseIndex = settings.phases.findIndex((phase) => phase.formatId === 'group-stage');
  const groupPhase = settings.phases[groupPhaseIndex];
  const groupConfig = groupPhase?.config;

  const patchGroupConfig = (key: string, value: number) => {
    if (groupPhase === undefined || typeof groupConfig !== 'object' || groupConfig === null) {
      return;
    }
    const nextPhases = settings.phases.map((phase, index) =>
      index === groupPhaseIndex
        ? { ...phase, config: { ...(groupConfig as Record<string, unknown>), [key]: value } }
        : phase,
    );
    patch({ phases: nextPhases });
  };

  // The KO phases whose named rounds can carry a per-round mode.
  const koRoundKeys = settings.phases
    .filter((phase) => phase.formatId === 'single-elimination')
    .flatMap((phase) =>
      NAMED_KO_ROUNDS.map((round) => ({
        key: `${phase.id}.${round.slug}` as RoundKey,
        label: settings.phases.length > 1 ? `${phase.label}: ${round.label}` : round.label,
      })),
    );

  const roundBestOf = (key: RoundKey): string => {
    const override = settings.matchRules.byRound[key];
    return override === undefined ? '' : String(override.bestOf);
  };

  const patchDefaultBestOf = (bestOf: number) => {
    patch({
      matchRules: {
        ...settings.matchRules,
        default: { ...settings.matchRules.default, bestOf },
      },
    });
  };

  const patchRoundBestOf = (key: RoundKey, raw: string) => {
    const value = raw.trim();

    // Rebuild without the key rather than deleting it, then re-add when set.
    const nextByRound: MatchRulesSettings['byRound'] = Object.fromEntries(
      Object.entries(settings.matchRules.byRound).filter(([existing]) => existing !== key),
    );

    if (value !== '') {
      // A round override inherits scoring and legsPerSet from the default; only
      // the number of legs changes per round.
      const rule: MatchRuleSet = { ...settings.matchRules.default, bestOf: Number(value) };
      nextByRound[key] = rule;
    }

    patch({ matchRules: { ...settings.matchRules, byRound: nextByRound } });
  };

  return (
    <div className="flex flex-col gap-8">
      {saved && <Alert tone="success">Die Einstellungen wurden gespeichert.</Alert>}
      {save.error !== null && <Alert tone="danger">{describeError(save.error)}</Alert>}

      <Section title="Allgemein">
        <FormField
          label="Turniername"
          value={settings.general.name}
          onChange={(event) =>
            patch({ general: { ...settings.general, name: event.target.value } })
          }
        />
        <FormField
          label="Datum"
          type="date"
          value={settings.general.date}
          onChange={(event) =>
            patch({ general: { ...settings.general, date: event.target.value } })
          }
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="Anzahl Boards"
            type="number"
            min={1}
            value={String(settings.general.boardCount)}
            onChange={(event) =>
              patch({ general: { ...settings.general, boardCount: Number(event.target.value) } })
            }
          />
          <FormField
            label="Maximale Teamanzahl"
            type="number"
            min={1}
            value={String(settings.general.maxTeams)}
            onChange={(event) =>
              patch({ general: { ...settings.general, maxTeams: Number(event.target.value) } })
            }
          />
        </div>
      </Section>

      <Section title="Anmeldung">
        {/* Opening and closing lives on the control page, because it also moves
            the tournament status — the server checks both. One switch, one
            place, no way for the two halves to contradict each other. */}
        <p className="text-fg-subtle text-sm">
          Die Anmeldung wird unter „Steuerung" geöffnet und geschlossen — sie ist derzeit{' '}
          <strong className="text-fg font-semibold">
            {isRegistrationOpen(tournament) ? 'geöffnet' : 'geschlossen'}
          </strong>
          .
        </p>
        <FormField
          label="Maximale Songlänge (Sekunden)"
          type="number"
          min={1}
          value={String(settings.registration.songMaxDurationSeconds)}
          onChange={(event) =>
            patch({
              registration: {
                ...settings.registration,
                songMaxDurationSeconds: Number(event.target.value),
              },
            })
          }
          hint="Gilt sofort für alle Teams — der Grenzwert steht nur hier, nicht im Code."
        />
        <Toggle
          label="Mehrere Teams je Feuerwehr erlauben"
          checked={settings.registration.allowMultipleTeamsPerFireDepartment}
          onChange={(value) =>
            patch({
              registration: {
                ...settings.registration,
                allowMultipleTeamsPerFireDepartment: value,
              },
            })
          }
        />
      </Section>

      <Section title="Spielmodus">
        <FormField
          label="Standard — Best of (Legs)"
          type="number"
          min={1}
          value={String(settings.matchRules.default.bestOf)}
          onChange={(event) => patchDefaultBestOf(Number(event.target.value))}
          hint="Gilt für jede Runde, für die unten kein abweichender Wert gesetzt ist."
        />

        <div className="flex flex-col gap-2">
          <span className="text-fg-subtle text-xs tracking-wide uppercase">
            Abweichender Modus je KO-Runde
          </span>
          <p className="text-fg-subtle text-xs">
            Leer lassen = Standard. So entsteht die Staffelung, z. B. Achtelfinale 5, Halbfinale 7,
            Finale 9.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {koRoundKeys.map(({ key, label }) => (
              <FormField
                key={key}
                label={label}
                type="number"
                min={1}
                placeholder={`Standard (${settings.matchRules.default.bestOf})`}
                value={roundBestOf(key)}
                onChange={(event) => patchRoundBestOf(key, event.target.value)}
              />
            ))}
          </div>
        </div>
      </Section>

      {groupConfig !== undefined && (
        <Section title="Gruppenphase">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label="Anzahl Gruppen"
              type="number"
              min={1}
              value={String(numberFrom(groupConfig, 'groupCount', 1))}
              onChange={(event) => patchGroupConfig('groupCount', Number(event.target.value))}
            />
            <FormField
              label="Qualifikanten je Gruppe"
              type="number"
              min={1}
              value={String(numberFrom(groupConfig, 'qualifiersPerGroup', 2))}
              onChange={(event) =>
                patchGroupConfig('qualifiersPerGroup', Number(event.target.value))
              }
            />
            <FormField
              label="Punkte pro Sieg"
              type="number"
              min={0}
              value={String(numberFrom(groupConfig, 'pointsForWin', 3))}
              onChange={(event) => patchGroupConfig('pointsForWin', Number(event.target.value))}
            />
            <FormField
              label="Punkte pro Unentschieden"
              type="number"
              min={0}
              value={String(numberFrom(groupConfig, 'pointsForDraw', 1))}
              onChange={(event) => patchGroupConfig('pointsForDraw', Number(event.target.value))}
            />
          </div>
        </Section>
      )}

      <div className="flex gap-3">
        <Button
          onClick={() => save.mutate(settings)}
          isLoading={save.isPending}
          disabled={draft === null}
        >
          Einstellungen speichern
        </Button>
        {draft !== null && (
          <Button
            variant="ghost"
            onClick={() => {
              setDraft(null);
              setSaved(false);
              save.reset();
            }}
          >
            Änderungen verwerfen
          </Button>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-line bg-surface flex flex-col gap-4 rounded-xl border p-5">
      <h2 className="text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-4">
      <span className="text-sm font-medium">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="size-5 accent-[var(--color-brand)]"
      />
    </label>
  );
}
